import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  Video,
  MessageSquare,
  ChevronDown,
  Package,
  MapPin,
  School,
  X,
} from "lucide-react";
import ModernSettingHeader from "@/components/settings/ModernSettingHeader";
import { getAllDeliverables, Deliverable, AllDeliverablesStats } from "@/actions/delivery-hub";

const months = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const monthShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 3 }, (_, i) => currentYear - i);

const statusConfig: Record<string, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  pending: { label: "Pending", color: "text-amber-600", bg: "bg-amber-50", icon: Clock },
  approved: { label: "Approved", color: "text-green-600", bg: "bg-green-50", icon: CheckCircle },
  needs_revision: { label: "Needs Revision", color: "text-red-600", bg: "bg-red-50", icon: AlertCircle },
};

const truncate = (str: string, len: number) => {
  if (!str) return "";
  return str.length > len ? str.slice(0, len) + "..." : str;
};

const DeliveryHubHome = () => {
  const navigate = useNavigate();
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [stats, setStats] = useState<AllDeliverablesStats>({ total: 0, pending: 0, approved: 0, needs_revision: 0 });
  const [loading, setLoading] = useState(true);

  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(currentYear);
  const [selectedSchools, setSelectedSchools] = useState<number[]>([]);
  const [schoolDropdownOpen, setSchoolDropdownOpen] = useState(false);
  const [schoolSearch, setSchoolSearch] = useState("");
  const schoolDropdownRef = useRef<HTMLDivElement>(null);

  const fetchDeliverables = async () => {
    setLoading(true);
    const result = await getAllDeliverables(filterMonth, filterYear);
    setDeliverables(result.deliverables);
    setStats(result.stats);
    setSelectedSchools([]);
    setLoading(false);
  };

  useEffect(() => {
    fetchDeliverables();
  }, [filterMonth, filterYear]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (schoolDropdownRef.current && !schoolDropdownRef.current.contains(e.target as Node)) {
        setSchoolDropdownOpen(false);
        setSchoolSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Unique schools from loaded deliverables
  const allSchools = useMemo(() => {
    const map = new Map<number, string>();
    deliverables.forEach((d) => {
      if (!map.has(d.school_id)) map.set(d.school_id, d.slate_name || String(d.school_id));
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [deliverables]);

  const filteredSchools = useMemo(
    () => allSchools.filter((s) => s.name.toLowerCase().includes(schoolSearch.toLowerCase())),
    [allSchools, schoolSearch]
  );

  const visibleDeliverables = useMemo(() => {
    if (selectedSchools.length === 0) return deliverables;
    return deliverables.filter((d) => selectedSchools.includes(d.school_id));
  }, [deliverables, selectedSchools]);

  const toggleSchool = (id: number) => {
    setSelectedSchools((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getDate()} ${monthShort[d.getMonth()]} ${d.getFullYear()}`;
  };

  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getDate()} ${monthShort[d.getMonth()]} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const getLocation = (d: Deliverable) => {
    const parts = [d.area_name, d.city_name].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  };

  return (
    <div className="w-full h-full overflow-auto bg-gray-50">
      {/* Sticky Header */}
      <ModernSettingHeader
        title="All Deliverables"
        description="Overview of all deliverables across all schools"
        icon={<Package className="h-5 w-5" />}
      >
        <div className="flex items-center justify-between">
          {/* Stats */}
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-500">
              Total: <span className="font-medium text-gray-900">{stats.total}</span>
            </span>
            <span className="text-gray-300">|</span>
            <span className="text-amber-600">
              <Clock size={13} className="inline mr-1 -mt-0.5" />
              Pending: <span className="font-medium">{stats.pending}</span>
            </span>
            <span className="text-gray-300">|</span>
            <span className="text-green-600">
              <CheckCircle size={13} className="inline mr-1 -mt-0.5" />
              Approved: <span className="font-medium">{stats.approved}</span>
            </span>
            <span className="text-gray-300">|</span>
            <span className="text-red-600">
              <AlertCircle size={13} className="inline mr-1 -mt-0.5" />
              Revision: <span className="font-medium">{stats.needs_revision}</span>
            </span>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            {/* School Multi-Select */}
            <div className="relative" ref={schoolDropdownRef}>
              <button
                onClick={() => setSchoolDropdownOpen((o) => !o)}
                className={`flex items-center gap-2 bg-white border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 transition-colors ${
                  selectedSchools.length > 0
                    ? "border-brand text-brand font-medium"
                    : "border-gray-200 text-gray-700"
                }`}
              >
                <School size={14} />
                {selectedSchools.length === 0
                  ? "All Schools"
                  : selectedSchools.length === 1
                  ? allSchools.find((s) => s.id === selectedSchools[0])?.name || "1 school"
                  : `${selectedSchools.length} schools`}
                {selectedSchools.length > 0 ? (
                  <span
                    className="ml-1 hover:text-red-500"
                    onClick={(e) => { e.stopPropagation(); setSelectedSchools([]); }}
                  >
                    <X size={13} />
                  </span>
                ) : (
                  <ChevronDown size={13} className="text-gray-400" />
                )}
              </button>

              {schoolDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-50">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      autoFocus
                      type="text"
                      value={schoolSearch}
                      onChange={(e) => setSchoolSearch(e.target.value)}
                      placeholder="Search schools..."
                      className="w-full px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    />
                  </div>
                  <div className="max-h-56 overflow-y-auto py-1">
                    {filteredSchools.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-gray-400">No schools found</div>
                    ) : (
                      filteredSchools.map((school) => {
                        const checked = selectedSchools.includes(school.id);
                        return (
                          <label
                            key={school.id}
                            className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSchool(school.id)}
                              className="accent-brand rounded"
                            />
                            <span className="text-sm text-gray-700 truncate">{school.name}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  {selectedSchools.length > 0 && (
                    <div className="p-2 border-t border-gray-100">
                      <button
                        onClick={() => { setSelectedSchools([]); setSchoolDropdownOpen(false); }}
                        className="w-full text-xs text-gray-500 hover:text-red-500 py-1"
                      >
                        Clear selection
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="relative">
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(Number(e.target.value))}
                className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-1.5 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
            <div className="relative">
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(Number(e.target.value))}
                className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-1.5 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </ModernSettingHeader>

      {/* Table - Full Width */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-brand" />
          </div>
        ) : visibleDeliverables.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            {deliverables.length === 0
              ? `No deliverables found for ${months.find((m) => m.value === filterMonth)?.label} ${filterYear}`
              : "No deliverables match the selected schools"}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden w-full">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-medium text-gray-500">School</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Title</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Reviews</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Comments</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Last Admin</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Last Client</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Created</th>
                </tr>
              </thead>
              <tbody>
                {visibleDeliverables.map((d) => {
                  const sc = statusConfig[d.status] || statusConfig.pending;
                  const StatusIcon = sc.icon;
                  const rl = d.review_links_summary;
                  const location = getLocation(d);
                  return (
                    <tr
                      key={d.id}
                      onClick={() => navigate(`/delivery-hub/schools/${d.school_id}`)}
                      className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      {/* School + Location */}
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 truncate max-w-[200px]">
                          {d.slate_name}
                        </div>
                        {location && (
                          <div className="flex items-center gap-1 text-[11px] text-gray-400 mt-0.5">
                            <MapPin size={10} />
                            <span className="truncate max-w-[180px]">{location}</span>
                          </div>
                        )}
                      </td>
                      {/* Title */}
                      <td className="px-4 py-3 text-gray-700 max-w-[220px]">
                        <div className="truncate">{d.title}</div>
                      </td>
                      {/* Type */}
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-gray-600">
                          {d.type === "blog" ? <FileText size={14} /> : <Video size={14} />}
                          <span className="capitalize">{d.type}</span>
                        </span>
                      </td>
                      {/* Status */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sc.color} ${sc.bg}`}>
                          <StatusIcon size={12} />
                          {sc.label}
                        </span>
                      </td>
                      {/* Reviews */}
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {rl && rl.total > 0 ? (
                          <span>{rl.approved}/{rl.total} approved</span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      {/* Comments (total + unread) */}
                      <td className="px-4 py-3">
                        {(d.comment_count || 0) > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <MessageSquare size={13} className="text-gray-400" />
                            <span className="text-xs text-gray-600">{d.comment_count}</span>
                            {(d.unread_comment_count || 0) > 0 && (
                              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand text-white text-[10px] font-semibold">
                                {d.unread_comment_count}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xs">-</span>
                        )}
                      </td>
                      {/* Last Admin Comment */}
                      <td className="px-4 py-3 max-w-[160px]">
                        {d.last_admin_comment ? (
                          <div>
                            <div className="text-xs text-gray-700 truncate" title={d.last_admin_comment.comment}>
                              {truncate(d.last_admin_comment.comment, 40)}
                            </div>
                            <div className="text-[10px] text-gray-400 mt-0.5">
                              {d.last_admin_comment.user_name.split(" ")[0]} &middot; {formatDateTime(d.last_admin_comment.created_at)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xs">-</span>
                        )}
                      </td>
                      {/* Last Client Comment */}
                      <td className="px-4 py-3 max-w-[160px]">
                        {d.last_client_comment ? (
                          <div>
                            <div className="text-xs text-gray-700 truncate" title={d.last_client_comment.comment}>
                              {truncate(d.last_client_comment.comment, 40)}
                            </div>
                            <div className="text-[10px] text-gray-400 mt-0.5">
                              {d.last_client_comment.user_name.replace(/\s*\(Client\)$/, "")} &middot; {formatDateTime(d.last_client_comment.created_at)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xs">-</span>
                        )}
                      </td>
                      {/* Created Date */}
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {formatDate(d.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryHubHome;
